import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Users,
  Building2,
  FolderKanban,
  ClipboardList,
  CheckSquare,
  Paperclip,
} from 'lucide-react';
import { crm } from '@/lib/crm-client';
import type { SearchHit, SearchKind } from '@epasu/core';
// Aus `constants`, nicht aus dem Dienst: dieses Modul hat keine Importe und
// zieht deshalb nichts vom Backend in den Browser-Bundle.
import { MIN_SEARCH_TERM } from '@epasu/core/constants';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useDebouncedSearch } from '@/lib/useDebouncedSearch';

/**
 * Eine Suche über alles, was eine Akte hat.
 *
 * Bisher hatte jede Liste ihr eigenes Suchfeld, das nur in ihr suchte. Wer
 * nicht wusste, ob eine Schule als Firma, Projekt oder Auftrag gemeint war,
 * musste die Seiten der Reihe nach durchgehen. Der Dienst dahinter
 * (`crm.search.query`) liefert bereits sortierte, gruppierbare Treffer mit
 * fertigem `href` — hier wird nur noch dargestellt und navigiert.
 */
const GRUPPEN: { kind: SearchKind; label: string; icon: typeof Users }[] = [
  { kind: 'contact', label: 'Kontakte', icon: Users },
  { kind: 'organization', label: 'Firmen', icon: Building2 },
  { kind: 'project', label: 'Projekte', icon: FolderKanban },
  { kind: 'order', label: 'Aufträge', icon: ClipboardList },
  { kind: 'task', label: 'Aufgaben', icon: CheckSquare },
  { kind: 'file', label: 'Dateien', icon: Paperclip },
];

const KUERZEL = navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Strg+K';

export function GlobalSearch() {
  const [offen, setOffen] = useState(false);
  const [suche, setSuche] = useState('');
  const [markiert, setMarkiert] = useState(0);

  const feld = useRef<HTMLInputElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  // Nur die Tastatur darf scrollen. `onMouseEnter` setzt die Markierung
  // ebenfalls — die Liste unter dem Zeiger wegzuziehen, während jemand die
  // Maus bewegt, wäre eine eigene Art von falsch. Deshalb löscht auch die
  // Maus die Flagge und verlässt sich nicht darauf, dass der Effekt sie
  // aufgeräumt hat: am Listenende gibt `setMarkiert` denselben Wert zurück,
  // React rendert nicht neu, und der Effekt kommt gar nicht erst dran.
  const perTastatur = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: treffer,
    loading: laedt,
    error: fehler,
  } = useDebouncedSearch(() => crm.search.query({ term: suche }), [suche], {
    enabled: offen,
  });

  useEffect(() => {
    setMarkiert(0);
  }, [treffer]);

  // Ohne das wandert die Markierung beim Pfeilen unter den Rand des Kastens,
  // und Enter springt zu einem Treffer, den man nie gesehen hat.
  useEffect(() => {
    if (!perTastatur.current) return;
    perTastatur.current = false;
    liste.current
      ?.querySelector(`[data-treffer="${markiert}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [markiert]);

  useEffect(() => {
    if (offen) feld.current?.focus();
  }, [offen]);

  const schliessen = () => {
    setOffen(false);
    setSuche('');
  };

  // Tastenkürzel weltweit, aber nicht während irgendwo getippt wird — sonst
  // kapert ⌘K/Strg+K eine Notiz oder ein anderes Textfeld.
  useEffect(() => {
    const aufTaste = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key === 'k')) return;
      const ziel = document.activeElement;
      const tippt =
        ziel instanceof HTMLElement &&
        (ziel.tagName === 'INPUT' ||
          ziel.tagName === 'TEXTAREA' ||
          ziel.isContentEditable);
      if (tippt) return;
      e.preventDefault();
      setOffen(true);
    };
    window.addEventListener('keydown', aufTaste);
    return () => window.removeEventListener('keydown', aufTaste);
  }, []);

  // Flache Liste in Gruppenreihenfolge — die Markierung läuft über alle
  // Treffer hinweg, nicht innerhalb einer Gruppe. `treffer` kommt bereits
  // sortiert vom Dienst, hier wird nur nach Art gruppiert dargestellt.
  const gruppiert = useMemo(() => {
    const liste = treffer ?? [];
    return GRUPPEN.map((g) => ({
      ...g,
      hits: liste.filter((t) => t.kind === g.kind),
    })).filter((g) => g.hits.length > 0);
  }, [treffer]);

  const flach = useMemo(() => gruppiert.flatMap((g) => g.hits), [gruppiert]);

  const springen = (hit: SearchHit) => {
    // `state.from` sagt der Zielseite, wohin ihr Zurück-Link führt — ohne das
    // landete man nach einem Treffer aus dem Overlay an einer Stelle, an der
    // man nie war.
    navigate(hit.href, { state: { from: location.pathname } });
    schliessen();
  };

  const aufTaste = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      perTastatur.current = true;
      setMarkiert((i) => Math.min(i + 1, flach.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      perTastatur.current = true;
      setMarkiert((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const t = flach[markiert];
      if (t) springen(t);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Sonst liest auch ein darunterliegender Dialog dasselbe Escape und
      // schließt sich gleich mit — derselbe Fehler, der im EntityPicker
      // gefunden und dort so behoben wurde.
      e.stopPropagation();
      schliessen();
    }
  };

  const begriff = suche.trim();
  // Dieselbe Untergrenze wie im Dienst, nicht eine zweite daneben — sonst
  // verspricht das Overlay eine Länge, die `searchAll` ablehnt.
  const begriffZuKurz = begriff.length > 0 && begriff.length < MIN_SEARCH_TERM;
  const gesucht = begriff.length >= MIN_SEARCH_TERM;
  // Ein Fehler zählt nur bei einer tatsächlich abgeschickten Suche — sonst
  // könnte er sich mit der „zu kurz“-Meldung überlappen.
  const zeigeFehler = gesucht && !!fehler;

  return (
    <>
      <button
        type="button"
        onClick={() => setOffen(true)}
        className={cn(
          'app-no-drag flex h-9 w-64 items-center gap-2 rounded-md border border-input bg-card px-3 text-left text-sm text-muted-foreground shadow-sm transition-colors',
          'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <Search className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 truncate">Suchen …</span>
        <kbd className="shrink-0 rounded border border-border bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
          {KUERZEL}
        </kbd>
      </button>

      {offen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[10vh]"
          onClick={schliessen}
        >
          <div
            className="flex max-h-[70vh] w-full max-w-xl flex-col rounded-lg border border-border bg-card shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Input
                ref={feld}
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                onKeyDown={aufTaste}
                placeholder="Suchen …"
                className="h-11 border-0 shadow-none focus-visible:ring-0"
              />
            </div>

            <div ref={liste} className="flex-1 overflow-auto scrollbar-thin py-1">
              {begriff.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  Suche nach Kontakten, Firmen, Projekten, Aufträgen, Aufgaben und
                  Dateien.
                </p>
              )}

              {begriffZuKurz && (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  Mindestens zwei Zeichen.
                </p>
              )}

              {zeigeFehler && (
                <p className="px-4 py-6 text-center text-sm text-destructive">{fehler}</p>
              )}

              {gesucht && !zeigeFehler && (
                <>
                  {/* Eine eigene Zeile statt eines Wechsels der ganzen Liste — sonst
                      leert und füllt sich der Kasten bei jedem Tastendruck neu. Die
                      zuletzt sichtbaren Treffer bleiben stehen, nur abgedunkelt,
                      solange die nächste Antwort unterwegs ist. */}
                  {laedt && (
                    <p className="px-4 pb-1 pt-2 text-xs text-muted-foreground">Lade …</p>
                  )}

                  {!laedt && flach.length === 0 && (
                    // Die durchsuchten Arten mitzunennen ist hier kein
                    // Formelkram: geschlossene Projekte und erledigte Aufgaben
                    // sind bewusst draußen, und wer die Aufgabe von gestern
                    // nicht findet, bekommt sonst keinen Hinweis darauf.
                    <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                      Nichts gefunden für „{begriff}“ — durchsucht wurden Kontakte,
                      Firmen, Projekte, Aufträge, Aufgaben und Dateien.
                    </p>
                  )}

                  {flach.length > 0 && (
                    <div className={cn(laedt && 'opacity-60')}>
                      {gruppiert.map((gruppe) => (
                        <div key={gruppe.kind}>
                          <div className="flex items-center gap-1.5 px-3 pb-1 pt-2 text-xs font-semibold text-muted-foreground">
                            <gruppe.icon className="h-3.5 w-3.5" />
                            {gruppe.label}
                          </div>
                          <ul>
                            {gruppe.hits.map((hit) => {
                              const index = flach.indexOf(hit);
                              return (
                                <li key={`${hit.kind}-${hit.id}`}>
                                  <button
                                    type="button"
                                    data-treffer={index}
                                    onMouseEnter={() => {
                                      perTastatur.current = false;
                                      setMarkiert(index);
                                    }}
                                    onClick={() => springen(hit)}
                                    className={cn(
                                      'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm',
                                      index === markiert
                                        ? 'bg-accent text-accent-foreground'
                                        : '',
                                    )}
                                  >
                                    <span className="min-w-0">
                                      <span className="block truncate">{hit.title}</span>
                                      {hit.subtitle && (
                                        <span className="block truncate text-xs text-muted-foreground">
                                          {hit.subtitle}
                                        </span>
                                      )}
                                    </span>
                                    {hit.badge && (
                                      <Badge variant="success" className="shrink-0">
                                        {hit.badge}
                                      </Badge>
                                    )}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
